import { ArrowLeftUniversal, ChevronDownUniversal, EditOutlined } from '@duxton-web/icons';
import { Button, Card, Divider, Dropdown, Flex, Input, Spin, Tag, Typography } from '@duxton-web/ui';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'grab-ui';

import {
  GET_UCD_CAMPAIGN,
  APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC,
  PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC,
  clearJourneyCampaignUserAudit,
  getJourneyCampaignCommsTimeline,
  getJourneyCampaignTemplateVariants,
  getJourneyCampaignUserAudit,
  previewJourneyCampaignSheetSync,
  applyJourneyCampaignSheetSync,
  getUCDCampaign,
  resetUCDCampaign,
  updateUCDCampaignStatus,
} from 'omega-rtc/actions/ucd';
import { getFeatureFlagConfig } from 'actions/newTridentExtra';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import {
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import { isRequestsPending } from 'omega-rtc/utils/request-state-utils';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import { getValueByFeatureFlag, hasPermission, hasTridentPermission } from 'omega-rtc/utils/user-utils';
import { UcdCampaignResourceName, UcdUniversalScope } from 'omega-rtc/data/trident-campaigns';
import { getCountryFlag } from './UCDCreateJourneyCampaign';
import {
  getJourneyCampaignProductOfferingLabel,
  getJourneyCampaignVerticalLabel,
} from './JourneyCampaignMetadataOptions';
import { JourneyCampaignSyncPreviewModal } from './JourneyCampaignSyncPreview';
import { buildJourneyCampaignSyncPreview, isSheetSyncPreviewForCampaigns } from 'omega-rtc/utils/journey-sheet-sync';
import './UCDHomePage.scss';

type Props = {
  params: {
    campaignID?: string;
    campaignId?: string;
    entityID?: string;
    entityId?: string;
  };
  router: { goBack: () => void; push: (path: string) => void };
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'success',
  DRAFT: 'warning',
  PENDING_APPROVAL: 'processing',
};
const EMPTY_AUDIT_REQUEST = {};

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString() : '—');
export const formatDateOnly = (value?: string) => {
  if (!value) {
    return '—';
  }

  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
};

export const getSafeHttpUrl = (value?: string) => {
  if (!value) {
    return undefined;
  }

  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : undefined;
  } catch (_) {
    return undefined;
  }
};

export const getJourneyCampaignActionDefinitions = (
  status: string,
  isSynced: boolean,
  hasUpdatePermission: boolean,
  hasActivePermission: boolean,
) => {
  if (status === 'DRAFT') {
    if (!hasUpdatePermission) {
      return [];
    }
    return [
      { key: 'sync', label: 'Sync from Sheet' },
      { key: 'submit', label: 'Submit for approval', disabled: !isSynced },
    ];
  }
  if (status === 'PENDING_APPROVAL') {
    if (!hasActivePermission) {
      return [];
    }
    return [{ key: 'approve', label: 'Approve' }, { key: 'reject', label: 'Reject' }];
  }
  if (status === 'ACTIVE') {
    if (!hasActivePermission) {
      return [];
    }
    return [{ key: 'deactivate', label: 'Deactivate' }];
  }
  return [];
};

export const shouldShowJourneyCampaignSupportingSections = (isSynced: boolean) => isSynced;

export const getJourneyCampaignStatus = (campaign: any, campaignDetails: any) => (
  _.toUpper(_.get(campaign, 'status') || _.get(campaignDetails, 'status') || '')
);

export const getJourneyCampaignGroupBackPath = (campaignGroupID?: string) => (
  campaignGroupID
    ? UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_GROUP_VIEW(campaignGroupID)
    : undefined
);

export const getJourneyCampaignSetupRows = (
  campaign: any,
  campaignDetails: any,
) => {
  const vertical = campaignDetails.vertical
    || _.get(campaignDetails, 'verticals[0]')
    || campaign.vertical
    || _.get(campaign, 'verticals[0]')
    || '';
  const productOffering = campaignDetails.productOffering
    || campaignDetails.CVP
    || campaign.productOffering
    || campaign.CVP
    || '';

  return [
    ['Campaign Creator', campaign.createdBy || '—'],
    ['Audience type', campaign.audienceType || campaign.audience || '—'],
    ['Journey type', campaignDetails.journeyType || campaign.journeyType || '—'],
    ['Vertical', vertical ? getJourneyCampaignVerticalLabel(vertical) : '—'],
    [
      'Product offering',
      productOffering
        ? getJourneyCampaignProductOfferingLabel(productOffering)
        : '—',
    ],
    ['Channel type', _.get(campaignDetails, 'channels', []).join(', ') || 'Not configured'],
  ];
};

const JourneyCampaignDetailPage = ({ params, router }: Props) => {
  const dispatch = useDispatch();
  const campaign = useSelector((state: any) => state.ucd.campaignView);
  const request = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(GET_UCD_CAMPAIGN),
    {},
  ));
  const user = useSelector((state: any) => state.app.user) || {};
  const countries = useSelector((state: any) => state.countries.entities || []);
  const featureFlagConfig = useSelector((state: any) => _.get(state, 'newTridents.featureFlagConfig', {}));
  const templateVariants = useSelector((state: any) => state.ucd.journeyCampaignTemplateVariants);
  const timeline = useSelector((state: any) => state.ucd.journeyCampaignCommsTimeline);
  const audit = useSelector((state: any) => state.ucd.journeyCampaignUserAudit);
  const auditEvents = _.get(audit, 'events', []);
  const auditRequest = useSelector((state: any) => (
    state.ucd.journeyCampaignUserAuditRequest || EMPTY_AUDIT_REQUEST
  ));
  const sheetSyncPreview = useSelector((state: any) => state.ucd.journeyCampaignSheetSyncPreview);
  const previewSheetSyncRequest = useSelector((state: any) => _.get(
    state.requests, getRequestStateKey(PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC), {},
  ));
  const applySheetSyncRequest = useSelector((state: any) => _.get(
    state.requests, getRequestStateKey(APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC), {},
  ));
  const updatedCampaignStatusTimestamp = useSelector((state: any) => state.ucd.updatedCampaignStatusTimestamp);
  // Journey routes use the legacy uppercase parameter names, unlike SmartComms.
  const campaignId = params.campaignID || params.campaignId || '';
  const projectId = params.entityID || params.entityId || '';
  const today = new Date();
  const defaultEndDate = today.toISOString().slice(0, 10);
  const defaultStartDate = new Date(today.getTime() - 86400000).toISOString().slice(0, 10);
  const [timelineStartDate, setTimelineStartDate] = useState(defaultStartDate);
  const [timelineEndDate, setTimelineEndDate] = useState(defaultEndDate);
  const [auditUserID, setAuditUserID] = useState('');
  const [isSheetSyncOpen, setIsSheetSyncOpen] = useState(false);
  const [hasAttemptedApply, setHasAttemptedApply] = useState(false);
  // A timeout may hide a successful apply. Never reuse an attempted token on this page.
  const attemptedPreviewTokens = useRef(new Set());

  useEffect(() => {
    dispatch(getUCDCampaign(projectId, campaignId));
    return () => { dispatch(resetUCDCampaign()); };
  }, [campaignId, dispatch, projectId]);

  useEffect(() => {
    dispatch(getFeatureFlagConfig());
  }, [dispatch]);

  const campaignRecord = campaign || {};
  const campaignDetails = _.get(campaignRecord, 'campaign', campaignRecord) || {};
  const isLoaded = campaign && (campaignRecord.campaignID || campaignRecord.ID || campaignDetails.ID) === campaignId;
  const journeyMetadata = _.get(campaignDetails, 'journeyMetadata', _.get(campaign, 'journeyMetadata', {}));
  const groups = _.get(journeyMetadata, 'groups', []);
  const isSynced = Boolean(journeyMetadata.lastSyncedAt);
  const status = getJourneyCampaignStatus(campaign, campaignDetails);
  const campaignName = campaignDetails.name || campaignDetails.code || campaignId;
  const countryID = campaignRecord.countryID || campaignDetails.countryID;
  const campaignGroupID = campaignRecord.campaignGroupID || campaignDetails.campaignGroupID;
  const campaignGroupBackPath = getJourneyCampaignGroupBackPath(campaignGroupID);
  const country = _.find(countries, (candidate: any) => (
    String(candidate.countryID) === String(countryID)
  ));
  const countryCode = _.get(country, 'code') || campaignRecord.countryCode || campaignDetails.countryCode || '';
  const countryName = _.get(country, 'name') || campaignRecord.countryName || campaignDetails.countryName || '—';
  const sourceUrl = getSafeHttpUrl(_.get(journeyMetadata, 'source.url'));
  const hasUpdatePermission = useMemo(() => getValueByFeatureFlag(
    // "Enbaled" is the deployed GrabX config key; retain its spelling for compatibility.
    _.get(featureFlagConfig, 'holosPortalPermissionEnbaled', false),
    hasPermission(user.userPermissions || {}, UcdCampaignResourceName, UcdUniversalScope, 'update'),
    hasTridentPermission(user.userPermissions || {}, '', '', 'update'),
  ), [featureFlagConfig, user.userPermissions]);
  const hasActivePermission = useMemo(() => getValueByFeatureFlag(
    _.get(featureFlagConfig, 'holosPortalPermissionEnbaled', false),
    hasPermission(user.userPermissions || {}, UcdCampaignResourceName, UcdUniversalScope, 'active'),
    hasTridentPermission(user.userPermissions || {}, '', '', 'active'),
  ), [featureFlagConfig, user.userPermissions]);
  const actions = useMemo(() => {
    const updateStatus = (nextStatus: string) => dispatch(
      updateUCDCampaignStatus(nextStatus, user.email, projectId, campaignId),
    );
    return getJourneyCampaignActionDefinitions(
      status,
      isSynced,
      hasUpdatePermission,
      hasActivePermission,
    ).map((action) => ({
      ...action,
      disabled: action.disabled || (action.key === 'sync' && !campaignGroupID),
      onClick: () => {
        if (action.key === 'sync' && campaignGroupID) {
          setHasAttemptedApply(false);
          setIsSheetSyncOpen(true);
          dispatch(previewJourneyCampaignSheetSync(campaignGroupID, { campaignID: campaignId, countryID, projectID: projectId }));
        } else if (action.key === 'submit') {
          updateStatus('PENDING_APPROVAL');
        } else if (action.key === 'approve') {
          updateStatus('ACTIVE');
        } else if (action.key === 'reject') {
          updateStatus('DRAFT');
        } else if (action.key === 'deactivate') {
          updateStatus('DEACTIVATED');
        }
      },
    }));
  }, [campaignGroupID, campaignId, countryID, dispatch, hasActivePermission, hasUpdatePermission, isSynced, projectId, status, user.email]);

  useEffect(() => {
    if (updatedCampaignStatusTimestamp) {
      dispatch(getUCDCampaign(projectId, campaignId));
    }
  }, [campaignId, dispatch, projectId, updatedCampaignStatusTimestamp]);

  useEffect(() => {
    if (hasAttemptedApply && !applySheetSyncRequest.isPending && applySheetSyncRequest.isSuccessful) {
      setHasAttemptedApply(false);
      setIsSheetSyncOpen(false);
      dispatch(getUCDCampaign(projectId, campaignId));
    }
  }, [applySheetSyncRequest.isPending, applySheetSyncRequest.isSuccessful,
    campaignId, dispatch, hasAttemptedApply, projectId]);

  useEffect(() => {
    if (isSynced) {
      dispatch(getJourneyCampaignTemplateVariants(projectId, campaignId));
    }
  }, [campaignId, dispatch, isSynced, projectId]);

  const previewTimeline = () => {
    if (!groups.length || !timelineStartDate || !timelineEndDate) {
      return;
    }
    dispatch(getJourneyCampaignCommsTimeline(projectId, campaignId, {
      startDate: timelineStartDate,
      endDate: timelineEndDate,
    }));
  };

  const clearAudit = () => {
    dispatch(clearJourneyCampaignUserAudit());
  };

  const loadAudit = () => {
    if (!auditUserID.trim() || !timelineStartDate || !timelineEndDate || auditRequest.isPending) {
      return;
    }
    dispatch(getJourneyCampaignUserAudit(projectId, campaignId, {
      userID: auditUserID.trim(),
      startDate: timelineStartDate,
      endDate: timelineEndDate,
    }));
  };

  const syncCampaigns = [{
    campaignID: campaignId, countryID, projectID: projectId, countryCode, countryName,
  }];
  const hasValidPreview = isSheetSyncPreviewForCampaigns(sheetSyncPreview, syncCampaigns);
  const hasUnusedPreviewToken = hasValidPreview
    && !attemptedPreviewTokens.current.has(sheetSyncPreview.previewToken);
  const previewCountries = hasValidPreview
    ? buildJourneyCampaignSyncPreview(sheetSyncPreview, syncCampaigns, countries) : [];

  if (isRequestsPending(request) || !isLoaded) {
    if (request.isFailure) {
      return <Alert message="Journey campaign could not be loaded" showIcon type="error" />;
    }
    return <Flex align="center" justify="center" style={{ minHeight: 320 }}><Spin /></Flex>;
  }

  return (
    <div className="ucd-basic journey-campaign-country-page">
      <Button
        icon={<ArrowLeftUniversal />}
        type="text"
        onClick={() => (campaignGroupBackPath
          ? router.push(campaignGroupBackPath)
          : router.goBack())}
      >
        Campaign Group
      </Button>
      <Flex align="center" className="journey-campaign-country-header" justify="space-between">
        <Typography.Title level={2} style={{ margin: 0 }}>{campaignName}</Typography.Title>
        <Flex gap="12px">
          {hasUpdatePermission && <Button icon={<EditOutlined />} onClick={() => router.push(UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_EDIT(projectId, campaignId))}>Edit</Button>}
          <Dropdown menu={{ items: actions }} disabled={!actions.length} trigger={['click']}>
            <Button disabled={!actions.length}>Actions <ChevronDownUniversal /></Button>
          </Dropdown>
        </Flex>
      </Flex>
      <Flex align="center" className={`journey-campaign-country-summary journey-campaign-status-${status.toLowerCase().replace(/_/g, '-') || 'default'}`} justify="space-between">
        <Flex align="center" gap="12px">
          <div className="journey-campaign-country-summary-flag">{getCountryFlag(countryCode)}</div>
          <Flex vertical>
            <Typography.Body>Country</Typography.Body>
            <Typography.Title level={4} style={{ margin: 0 }}>{countryName}</Typography.Title>
          </Flex>
        </Flex>
        <Flex gap="32px">
          <Flex vertical>
            <Typography.Body>Source</Typography.Body>
            {sourceUrl ? <a href={sourceUrl} rel="noreferrer" target="_blank">Google Sheet</a> : <Typography.Body>—</Typography.Body>}
          </Flex>
          <Flex vertical>
            <Typography.Body>Status</Typography.Body>
            <Tag color={STATUS_COLORS[status] || 'default'}>{campaign.status || campaignDetails.status}</Tag>
          </Flex>
        </Flex>
      </Flex>
      <Card bordered className={`journey-campaign-country-details journey-campaign-status-${status.toLowerCase().replace(/_/g, '-') || 'default'}`} title="Campaign details">
        <div className="journey-campaign-country-details-grid">
          <section className="journey-campaign-country-detail-section">
            <Typography.Body strong>Campaign setup</Typography.Body>
            {getJourneyCampaignSetupRows(campaign, campaignDetails).map(([label, value]) => <div className="journey-campaign-country-detail-row" key={label}><Typography.Body>{label}</Typography.Body><Typography.Body className="journey-campaign-country-detail-value" strong>{value}</Typography.Body></div>)}
          </section>
          <section className="journey-campaign-country-detail-section">
            <Typography.Body strong>Campaign schedule</Typography.Body>
            <div className="journey-campaign-country-detail-row"><Typography.Body>Start date</Typography.Body><Typography.Body className="journey-campaign-country-detail-value" strong>{formatDateOnly(campaignDetails.startDate || campaignDetails.start)}</Typography.Body></div>
            <div className="journey-campaign-country-detail-row"><Typography.Body>End date</Typography.Body><Typography.Body className="journey-campaign-country-detail-value" strong>{formatDateOnly(campaignDetails.endDate || campaignDetails.end)}</Typography.Body></div>
          </section>
          <section className="journey-campaign-country-detail-section">
            <Typography.Body strong>Google Sheet activity</Typography.Body>
            <div className="journey-campaign-country-detail-row"><Typography.Body>Last synced</Typography.Body><Typography.Body className="journey-campaign-country-detail-value" strong>{journeyMetadata.lastSyncedAt ? formatDate(journeyMetadata.lastSyncedAt) : 'No sync history'}</Typography.Body></div>
            <div className="journey-campaign-country-detail-row"><Typography.Body>Last Google Sheet updated</Typography.Body><Typography.Body className="journey-campaign-country-detail-value" strong>{formatDate(_.get(journeyMetadata, 'source.lastUpdatedAt'))}</Typography.Body></div>
          </section>
        </div>
      </Card>
      {!shouldShowJourneyCampaignSupportingSections(isSynced) ? (
        null
      ) : (
        <React.Fragment>
          <Divider />
          <Typography.Title level={4}>Preview Comms Timeline</Typography.Title>
          <Flex gap="12px" style={{ marginBottom: 16 }}>
            <Input
              aria-label="Timeline start date"
              type="date"
              value={timelineStartDate}
              onChange={(event: any) => {
                setTimelineStartDate(event.target.value);
                clearAudit();
              }}
            />
            <Input
              aria-label="Timeline end date"
              type="date"
              value={timelineEndDate}
              onChange={(event: any) => {
                setTimelineEndDate(event.target.value);
                clearAudit();
              }}
            />
            <Button disabled={!groups.length} type="primary" onClick={previewTimeline}>Preview</Button>
          </Flex>
          {_.get(timeline, 'buckets', []).map((bucket: any) => (
            <div key={bucket.timestamp} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <span style={{ minWidth: 160 }}>{formatDate(bucket.timestamp)}</span>
              {bucket.comms.filter((item: any) => item.count > 0).map((item: any) => (
                <span key={item.groupID} style={{ background: '#00b14f', color: '#fff', minWidth: Math.max(32, item.count / 10), padding: '2px 6px' }}>{item.groupID}: {item.count}</span>
              ))}
            </div>
          ))}
          <Divider />
          <Typography.Title level={4}>Template variant mapping</Typography.Title>
          {_.get(templateVariants, 'groups', groups).length ? _.get(templateVariants, 'groups', groups).map((group: any) => (
            <div key={group.groupID} style={{ border: '1px solid #d9d9d9', borderRadius: 8, marginBottom: 12, padding: 16 }}>
              <strong>{group.groupID}</strong> · {group.channel} · {group.numberOfVariants} variants · Control Group {_.get(group, 'experiment.controlGroupPercentage', 0)}%
              {_.get(group, 'variants', []).map((variant: any) => (
                <div key={variant.name} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee' }}>
                  <strong>{variant.name}</strong><br />
                  {Object.entries(variant.criteria || {}).map(([key, value]) => <span key={key} style={{ marginRight: 12 }}>{key}: {JSON.stringify(value)}</span>)}<br />
                  {getSafeHttpUrl(_.get(variant, 'template.url')) ? <a href={getSafeHttpUrl(_.get(variant, 'template.url'))} rel="noreferrer" target="_blank">{_.get(variant, 'template.ID')}</a> : <Typography.Body>{_.get(variant, 'template.ID', 'No template link')}</Typography.Body>}
                </div>
              ))}
            </div>
          )) : <Typography.Paragraph>No variants are configured.</Typography.Paragraph>}
          <Typography.Title level={4}>Audit User</Typography.Title>
          <Flex gap="12px">
            <Input
              aria-label="Audit user ID"
              placeholder="User ID"
              value={auditUserID}
              onChange={(event: any) => {
                setAuditUserID(event.target.value);
                clearAudit();
              }}
            />
            <Button
              disabled={!auditUserID.trim() || auditRequest.isPending}
              loading={auditRequest.isPending}
              onClick={loadAudit}
            >
              Audit user
            </Button>
          </Flex>
          {auditRequest.isPending && (
            <Typography.Paragraph role="status">Loading journey events…</Typography.Paragraph>
          )}
          {auditRequest.isFailure && (
            <Alert message="Could not load journey events. Check the filters and try again." showIcon type="error" />
          )}
          {auditRequest.isSuccessful && !auditEvents.length && (
            <Typography.Paragraph role="status">No journey events were found for this user and date range.</Typography.Paragraph>
          )}
          {auditRequest.isSuccessful && auditEvents.map((event: any) => (
            <div key={`${event.type}-${event.timestamp}`} style={{ marginTop: 8 }}>
              {formatDate(event.timestamp)} — {event.type}
            </div>
          ))}
        </React.Fragment>
      )}
      <JourneyCampaignSyncPreviewModal
        applyDisabled={!campaignGroupID || !hasUpdatePermission || !hasUnusedPreviewToken}
        applyError={hasAttemptedApply && applySheetSyncRequest.isFailure}
        applying={applySheetSyncRequest.isPending}
        countries={previewCountries}
        expandSingleCountry
        loading={previewSheetSyncRequest.isPending
          || (!sheetSyncPreview && !previewSheetSyncRequest.isFailure)}
        onApply={() => {
          if (!campaignGroupID || !hasUpdatePermission || applySheetSyncRequest.isPending
            || !hasValidPreview
            || attemptedPreviewTokens.current.has(sheetSyncPreview.previewToken)) return;
          attemptedPreviewTokens.current.add(sheetSyncPreview.previewToken);
          setHasAttemptedApply(true);
          dispatch(applyJourneyCampaignSheetSync(
            campaignGroupID, sheetSyncPreview.previewToken, syncCampaigns,
          ));
        }}
        onClose={() => {
          if (applySheetSyncRequest.isPending) return;
          setHasAttemptedApply(false);
          setIsSheetSyncOpen(false);
        }}
        onRetry={() => {
          setHasAttemptedApply(false);
          dispatch(previewJourneyCampaignSheetSync(campaignGroupID, syncCampaigns));
        }}
        open={isSheetSyncOpen}
        previewError={previewSheetSyncRequest.isFailure}
      />
    </div>
  );
};

const AuthorizedJourneyCampaignDetailPage = (props: Props) => (
  <ShowIfAuthorized
    conditions={[{ resource: UCD_RESOURCE_NAMES.campaign, action: UCD_ACTION_TYPES.read }]}
    unauthorizedContent={<Alert message="You don't have access to Holos campaign" showIcon type="error" />}
  >
    <JourneyCampaignDetailPage {...props} />
  </ShowIfAuthorized>
);

export default AuthorizedJourneyCampaignDetailPage;
